import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import mapboxgl from "mapbox-gl";
import { fetchTripById, ApiTrip } from "../../service/travelerMapTripService";
import { transformTrip, TransformedTrip } from "../utils/tripTransformer";
import { calculateDetailPopupPosition } from "../utils/flightPopupHelpers";
import { PopupPassenger } from "../component/FlightPopup";

// Constants for popup dimensions
const HOVER_POPUP_WIDTH = 220;
const DETAIL_POPUP_WIDTH = 405;
const POPUP_OFFSET = 20;
const POPUP_SPACING = 5;
const DEFAULT_PASSENGER_CARD_INNER_WIDTH = 140;
const VIEWPORT_EXIT_CLOSE_DELAY = 500; // milliseconds

interface UseFlightPopupParams {
    map: mapboxgl.Map;
    lngLat: mapboxgl.LngLatLike;
    info: {
        type: 'flight' | 'airport';
        flightNumber: string;
        dep: string;
        arr: string;
        passengers?: PopupPassenger[];
        tripCount: number;
        isCurrentFlightIssue: boolean;
        isFutureFlightIssue: boolean;
        processedFlightHistoryId?: string | number;
        recordLocator?: string;
    };
    popupShouldBeOnLeft: boolean;
    isExpanded: boolean;
    onClose: () => void;
}

interface UseFlightPopupReturn {
    hoverPosition: { x: number; y: number } | null;
    detailPopupPosition: { left: number; top: number } | null;
    selectedPassenger: PopupPassenger | null;
    transformedTrip: TransformedTrip | null;
    loading: boolean;
    isPopupOnLeft: boolean;
    passengerCardWidth: number;
    handlePassengerClick: (passenger: PopupPassenger) => Promise<void>;
    popupRef: React.RefObject<HTMLDivElement>;
    detailRef: React.RefObject<HTMLDivElement>;
    passengerCardsRef: React.RefObject<(HTMLDivElement | null)[]>;
}

export const useFlightPopup = ({
    map,
    lngLat,
    info,
    popupShouldBeOnLeft,
    isExpanded,
    onClose,
}: UseFlightPopupParams): UseFlightPopupReturn => {
    const popupRef = useRef<HTMLDivElement>(null);
    const detailRef = useRef<HTMLDivElement>(null);
    const passengerCardsRef = useRef<(HTMLDivElement | null)[]>([]);
    const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number } | null>(null);
    const [selectedPassenger, setSelectedPassenger] = useState<PopupPassenger | null>(null);
    const [transformedTrip, setTransformedTrip] = useState<TransformedTrip | null>(null);
    const [loading, setLoading] = useState(false);
    const [isPopupOnLeft, setIsPopupOnLeft] = useState<boolean>(false);
    const [detailPopupPosition, setDetailPopupPosition] = useState<{ left: number; top: number } | null>(null);
    const [passengerCardWidth, setPassengerCardWidth] = useState<number>(DEFAULT_PASSENGER_CARD_INNER_WIDTH);

    // Reset passenger selection when flight info changes
    useEffect(() => {
        setSelectedPassenger(null);
        setTransformedTrip(null);
        setLoading(false);
        setIsPopupOnLeft(false);
        setDetailPopupPosition(null);
    }, [info.flightNumber, info.dep, info.arr, info.tripCount]);

    // Calculate dynamic width for passenger cards based on content length
    // Memoized to avoid unnecessary recalculations
    const calculatedWidth = useMemo(() => {
        if (!info.passengers || info.passengers.length === 0) {
            return DEFAULT_PASSENGER_CARD_INNER_WIDTH;
        }

        // Create temporary element for text measurement
        const measureElement = document.createElement('div');
        measureElement.style.position = 'absolute';
        measureElement.style.visibility = 'hidden';
        measureElement.style.whiteSpace = 'nowrap';
        measureElement.style.fontFamily = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
        measureElement.style.fontSize = '12px';
        measureElement.style.fontWeight = '500';
        document.body.appendChild(measureElement);

        let maxWidth = DEFAULT_PASSENGER_CARD_INNER_WIDTH;

        info.passengers.forEach((passenger) => {
            measureElement.textContent = passenger.name;
            const nameWidth = measureElement.offsetWidth;

            measureElement.textContent = passenger.group;
            const groupWidth = measureElement.offsetWidth;

            maxWidth = Math.max(maxWidth, nameWidth, groupWidth);
        });

        document.body.removeChild(measureElement);

        // Add padding (8px left + 8px right = 16px total) + additional spacing
        return maxWidth + 96;
    }, [info.passengers]);

    useEffect(() => {
        setPassengerCardWidth(calculatedWidth);
    }, [calculatedWidth]);

    // Memoized popup positioning function
    const placePopup = useCallback((point: { x: number; y: number }) => {
        let result: { x: number; y: number } = { x: point.x, y: point.y };
        let isOnLeft = false;

        const mapContainerRect = map.getContainer().getBoundingClientRect();

        const totalWidth = selectedPassenger
            ? HOVER_POPUP_WIDTH + POPUP_SPACING + DETAIL_POPUP_WIDTH
            : HOVER_POPUP_WIDTH;

        const pointScreenX = point.x;

        // Determine boundaries based on expanded mode
        const topBoundary = isExpanded ? 0 : mapContainerRect.top;
        const bottomBoundary = isExpanded ? window.innerHeight : mapContainerRect.bottom;
        const leftBoundary = isExpanded ? 0 : mapContainerRect.left;
        const rightBoundary = isExpanded ? window.innerWidth : mapContainerRect.right;

        const availableRightSpace = rightBoundary - pointScreenX;
        const availableLeftSpace = pointScreenX - leftBoundary;

        // Use pre-calculated positioning to avoid flickering
        isOnLeft = popupShouldBeOnLeft;

        // Calculate hover popup position
        let left = isOnLeft ? point.x - (HOVER_POPUP_WIDTH + POPUP_OFFSET) : point.x + POPUP_OFFSET;
        let top = point.y;

        const estimatedPopupHeight = 200;

        // Try to get actual popup element for more accurate positioning
        const hoverElement = document.querySelector('.hoverContainer, .locationContainer') as HTMLElement;
        const actualPopupHeight = hoverElement ? hoverElement.getBoundingClientRect().height : estimatedPopupHeight;

        // Vertical positioning logic with boundary constraints
        const availableSpaceAbove = point.y - topBoundary;
        const availableSpaceBelow = bottomBoundary - point.y;

        if (availableSpaceAbove >= actualPopupHeight) {
            top = point.y - actualPopupHeight;
        } else if (availableSpaceBelow >= actualPopupHeight) {
            top = point.y;
        } else {
            if (availableSpaceAbove > availableSpaceBelow) {
                top = topBoundary;
            } else {
                top = Math.max(topBoundary, bottomBoundary - actualPopupHeight);
            }
        }

        // Ensure popup doesn't overflow boundaries
        top = Math.max(topBoundary, Math.min(top, bottomBoundary - actualPopupHeight));
        left = Math.max(leftBoundary, Math.min(left, rightBoundary - HOVER_POPUP_WIDTH));

        result = { x: left, y: top };

        // Update the left/right state for passenger popup positioning
        setIsPopupOnLeft(isOnLeft);
        return result;
    }, [map, selectedPassenger, isExpanded, popupShouldBeOnLeft]);

    // Keep popup anchored to map position
    useEffect(() => {
        const updatePosition = () => {
            const projected = map.project(lngLat);
            const mapContainerRect = map.getContainer().getBoundingClientRect();

            const screenX = projected.x + mapContainerRect.left;
            const screenY = projected.y + mapContainerRect.top;

            setPosition({ x: screenX, y: screenY });
            const pos = placePopup({ x: screenX, y: screenY });
            setHoverPosition(pos);
        };
        updatePosition();

        map.on("move", updatePosition);
        map.on("zoom", updatePosition);
        map.on("resize", updatePosition);
        return () => {
            map.off("move", updatePosition);
            map.off("zoom", updatePosition);
            map.off("resize", updatePosition);
        };
    }, [map, lngLat, selectedPassenger, isExpanded, placePopup]);

    // Update detail popup position when hover position or selection changes
    useEffect(() => {
        if (selectedPassenger && hoverPosition) {
            const mapContainerRect = map.getContainer().getBoundingClientRect();
            const position = calculateDetailPopupPosition({
                selectedPassenger,
                hoverPosition,
                isPopupOnLeft,
                isExpanded,
                mapContainerRect
            });
            setDetailPopupPosition(position);
        } else {
            setDetailPopupPosition(null);
        }
    }, [selectedPassenger, hoverPosition, isPopupOnLeft, isExpanded, map]);

    // Close popup when clicking outside or pressing ESC
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                popupRef.current &&
                !popupRef.current.contains(e.target as Node) &&
                (!detailRef.current || !detailRef.current.contains(e.target as Node))
            ) {
                onClose();
            }
        };
        const handleEsc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEsc);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEsc);
        };
    }, [onClose]);

    // Close popup with delay when flight/airport exits viewport
    useEffect(() => {
        const checkViewportBounds = () => {
            // Normalize lngLat to LngLat instance
            const lngLatInstance = Array.isArray(lngLat)
                ? new mapboxgl.LngLat(lngLat[0], lngLat[1])
                : lngLat instanceof mapboxgl.LngLat
                    ? lngLat
                    : new mapboxgl.LngLat('lng' in lngLat ? lngLat.lng : lngLat.lon, lngLat.lat);

            const bounds = map.getBounds();
            if (!bounds) return; // Map bounds not yet available
            const isInViewport = bounds.contains(lngLatInstance);

            if (!isInViewport) {
                // Flight/airport is outside viewport, start close timeout
                if (closeTimeoutRef.current === null) {
                    closeTimeoutRef.current = setTimeout(() => {
                        onClose();
                        closeTimeoutRef.current = null;
                    }, VIEWPORT_EXIT_CLOSE_DELAY);
                }
            } else {
                // Flight/airport is back in viewport, cancel any pending close
                if (closeTimeoutRef.current !== null) {
                    clearTimeout(closeTimeoutRef.current);
                    closeTimeoutRef.current = null;
                }
            }
        };

        // Initial check
        checkViewportBounds();

        // Listen to map movement
        map.on("move", checkViewportBounds);
        map.on("zoom", checkViewportBounds);

        return () => {
            // Clear pending timeout on cleanup
            if (closeTimeoutRef.current !== null) {
                clearTimeout(closeTimeoutRef.current);
                closeTimeoutRef.current = null;
            }
            map.off("move", checkViewportBounds);
            map.off("zoom", checkViewportBounds);
        };
    }, [map, lngLat, onClose]);

    // Handle passenger click and detail fetch
    const handlePassengerClick = useCallback(async (p: PopupPassenger) => {
        setSelectedPassenger(p);
        setTransformedTrip(null);
        setLoading(true);

        try {
            const apiTripDetail: ApiTrip = await fetchTripById(p.tripId);
            const pfhid = p.pfhId;
            const passengerName = p.name;
            const transformed = transformTrip(apiTripDetail, pfhid, passengerName);
            setTransformedTrip(transformed);
        } catch (err) {
            setTransformedTrip(null);
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        hoverPosition,
        detailPopupPosition,
        selectedPassenger,
        transformedTrip,
        loading,
        isPopupOnLeft,
        passengerCardWidth,
        handlePassengerClick,
        popupRef,
        detailRef,
        passengerCardsRef,
    };
};
